import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
  TextField,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
} from "@mui/material";
import axios from "axios";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import FUNCTIONS_URLS from '../../../../Config/functions';


const IMPORT_URL = FUNCTIONS_URLS.ClientHistoryImportToolHttp;
const EXTRACT_PDF_URL = FUNCTIONS_URLS.ExtractContractMetadataFromPdfHttp;

const primaryButtonSx = {
  background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
  color: "#fff",
  WebkitTextFillColor: "#fff",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
    opacity: 0.9,
  },
  "&.Mui-disabled": {
    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
    color: "#fff",
    WebkitTextFillColor: "#fff",
    opacity: 0.45,
  },
};

const dangerButtonSx = {
  bgcolor: "#f52a2d",
  color: "#fff",
  WebkitTextFillColor: "#fff",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": { bgcolor: "#d82022" },
  "&.Mui-disabled": {
    bgcolor: "#f52a2d",
    color: "#fff",
    WebkitTextFillColor: "#fff",
    opacity: 0.45,
  },
};

const outlinedButtonSx = {
  color: "#fff",
  WebkitTextFillColor: "#fff",
  borderColor: "rgba(42, 245, 152, 0.45)",
  textTransform: "none",
  "&:hover": {
    borderColor: "#2af598",
    backgroundColor: "rgba(42, 245, 152, 0.08)",
  },
  "&.Mui-disabled": {
    color: "#fff",
    WebkitTextFillColor: "#fff",
    borderColor: "rgba(42, 245, 152, 0.25)",
    opacity: 0.55,
  },
};

function formatJsonError(e) {
  return e?.message || "Invalid JSON";
}

function safeString(x) {
  if (x === null || x === undefined) return "";
  return String(x);
}

export default function ClientHistoryImportTool() {
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [pdfFileName, setPdfFileName] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [extractingPdf, setExtractingPdf] = useState(false);

  const [fileName, setFileName] = useState(null);
  const [rawJson, setRawJson] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedEventIds, setSelectedEventIds] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [rollbackBackupId, setRollbackBackupId] = useState("");
  const [rollbackResult, setRollbackResult] = useState(null);

  const legacyCandidates = useMemo(() => {
    const arr = payload?.legacy_events_candidates;
    return Array.isArray(arr) ? arr : [];
  }, [payload]);

  const selectedEvents = useMemo(() => {
    if (!legacyCandidates.length) return [];
    return legacyCandidates.filter((e, idx) => {
      const key = e.eventId || `${e.type_guess || "unknown"}|${e.amount ?? ""}|${e.date ?? ""}|${e.source_file ?? ""}|${idx}`;
      return !!selectedEventIds[key];
    });
  }, [legacyCandidates, selectedEventIds]);

  const contract = useMemo(() => {
    if (!payload) return null;
    return {
      contract_open_date: payload.contract_open_date || null,
      first_wallet_address: payload.first_wallet_address || null,
      withdrawal_wallet_addresses: payload.withdrawal_wallet_addresses || [],
      fiddux_internal_wallet_address: payload.fiddux_internal_wallet_address || payload?.user?.userId || null,
      initial_deposit_amount: payload.initial_deposit_amount ?? null,
      source_meta: payload.source_meta || null,
    };
  }, [payload]);

  const userId = payload?.user?.userId || null;
  const userEmail = payload?.user?.email || null;

  useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const q = query(collection(DATABASE, "users"), where("role", "==", 1));
        const snap = await getDocs(q);
        const users = snap.docs
          .map((d) => ({
            id: d.id,
            email: d.data().email || "",
            name: (d.data().full_name || d.data().name || "").trim() || d.data().email || "",
          }))
          .filter((u) => u.email)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        setUserOptions(users);
      } catch (e) {
        // non-fatal
        console.error(e);
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, []);

  const generateJson = async () => {
    if (!selectedUser?.id) {
      setError("Please select a client first");
      return;
    }

    setGenerating(true);
    setError(null);
    setPreview(null);
    setApplyResult(null);
    setRollbackResult(null);

    try {
      const userIdLocal = selectedUser.id;
      const emailLocal = selectedUser.email;

      let existingContract = null;
      try {
        const cmSnap = await getDoc(doc(DATABASE, "contract_metadata", userIdLocal));
        if (cmSnap.exists()) existingContract = cmSnap.data();
      } catch {
        // ignore
      }

      const generated = {
        user: { userId: userIdLocal, email: emailLocal },
        contract_open_date: existingContract?.contract_open_date || null,
        contract_open_date_candidates: [],
        first_wallet_address: existingContract?.first_wallet_address || null,
        withdrawal_wallet_addresses: Array.isArray(existingContract?.withdrawal_wallet_addresses)
          ? existingContract.withdrawal_wallet_addresses
          : [],
        fiddux_internal_wallet_address: existingContract?.fiddux_internal_wallet_address || userIdLocal,
        initial_deposit_amount: existingContract?.initial_deposit_amount ?? null,
        initial_deposit_amount_candidates: [],
        legacy_events_candidates: [],
        source_meta: {
          generated_by: "ClientHistoryImportTool",
          generated_at: new Date().toISOString(),
          note:
            "Template JSON generated in the Admin Panel. Fill contract_open_date / wallet(s) / initial_deposit based on the client's contract, then Preview/Apply.",
        },
      };

      setPayload(generated);
      setFileName(`generated_${userIdLocal}.json`);
      setRawJson(JSON.stringify(generated, null, 2));
      setSelectedEventIds({});
    } catch (e) {
      setError(e?.message || "Failed to generate JSON");
    } finally {
      setGenerating(false);
    }
  };

  const downloadCurrentJson = () => {
    if (!rawJson) return;
    const blob = new Blob([rawJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "client_history.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onPickPdf = async (evt) => {
    setError(null);
    const file = evt.target.files?.[0];
    if (!file) return;
    setPdfFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
      }
      setPdfBase64(btoa(binary));
    } catch (e) {
      setPdfBase64(null);
      setError(e?.message || "Failed to read PDF");
    }
  };

  const extractFromPdf = async () => {
    if (!pdfBase64) {
      setError("Please choose a PDF first");
      return;
    }
    if (!payload?.user?.userId) {
      setError("Generate JSON first (select client → Generate JSON) so we know which user to fill");
      return;
    }
    setExtractingPdf(true);
    setError(null);
    try {
      const res = await axios.post(EXTRACT_PDF_URL, { pdfBase64, fileName: pdfFileName });
      const extracted = res?.data?.extracted || {};

      const next = {
        ...payload,
        contract_open_date: extracted.contract_open_date || payload.contract_open_date || null,
        contract_open_date_candidates: extracted.contract_open_date_candidates || payload.contract_open_date_candidates || [],
        first_wallet_address: extracted.first_wallet_address || payload.first_wallet_address || null,
        withdrawal_wallet_addresses: extracted.withdrawal_wallet_addresses || payload.withdrawal_wallet_addresses || [],
        initial_deposit_amount: extracted.initial_deposit_amount ?? payload.initial_deposit_amount ?? null,
        initial_deposit_amount_candidates: extracted.initial_deposit_amount_candidates || payload.initial_deposit_amount_candidates || [],
        source_meta: {
          ...(payload.source_meta || {}),
          pdf_extraction: {
            fileName: res?.data?.file?.fileName || pdfFileName || null,
            sha256: res?.data?.file?.sha256 || null,
            pages: res?.data?.file?.pages || null,
            extracted_at: new Date().toISOString(),
            note: res?.data?.note || null,
          },
        },
      };

      setPayload(next);
      setFileName(fileName || `generated_${payload.user.userId}.json`);
      setRawJson(JSON.stringify(next, null, 2));
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.details || e.message);
    } finally {
      setExtractingPdf(false);
    }
  };

  const onPickFile = async (evt) => {
    setError(null);
    setPreview(null);
    setApplyResult(null);
    setRollbackResult(null);

    const file = evt.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const text = await file.text();
      setRawJson(text);
      const parsed = JSON.parse(text);
      setPayload(parsed);
      setSelectedEventIds({});
    } catch (e) {
      setPayload(null);
      setRawJson(null);
      setSelectedEventIds({});
      setError(formatJsonError(e));
    }
  };

  const toggleAll = (checked) => {
    const next = {};
    legacyCandidates.forEach((e, idx) => {
      const key = e.eventId || `${e.type_guess || "unknown"}|${e.amount ?? ""}|${e.date ?? ""}|${e.source_file ?? ""}|${idx}`;
      next[key] = checked;
    });
    setSelectedEventIds(next);
  };

  const runPreview = async () => {
    if (!userId || !contract) {
      setError("Missing userId/contract in JSON payload");
      return;
    }
    if (selectedEvents.length > 200) {
      setError("Too many legacy events selected (max 200). Import in smaller batches.");
      return;
    }
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await axios.post(IMPORT_URL, {
        action: "preview",
        userId,
        contract,
        legacyEvents: selectedEvents,
        importedBy: null,
      });
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.details || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!userId || !contract) {
      setError("Missing userId/contract in JSON payload");
      return;
    }
    if (selectedEvents.length > 200) {
      setError("Too many legacy events selected (max 200). Import in smaller batches.");
      return;
    }
    setLoading(true);
    setError(null);
    setApplyResult(null);
    try {
      const res = await axios.post(IMPORT_URL, {
        action: "apply",
        userId,
        contract,
        legacyEvents: selectedEvents,
        importedBy: null,
      });
      setApplyResult(res.data);
      if (res.data?.backupId) setRollbackBackupId(res.data.backupId);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.details || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runRollback = async () => {
    if (!rollbackBackupId.trim()) {
      setError("Please provide a backupId");
      return;
    }
    setLoading(true);
    setError(null);
    setRollbackResult(null);
    try {
      const res = await axios.post(IMPORT_URL, {
        action: "rollback",
        backupId: rollbackBackupId.trim(),
      });
      setRollbackResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.details || e.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = Object.values(selectedEventIds).filter(Boolean).length;

  return (
    <Box>
      <Typography variant="h6" sx={{ color: "#2af598", mb: 2 }}>
        Client History Import (preview → apply → rollback)
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        This tool writes to new collections only: <b>contract_metadata</b> and <b>legacy_transactions</b>. It creates a backup id for rollback.
      </Alert>

      <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
            Generate JSON (template)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Autocomplete
                options={userOptions}
                loading={loadingUsers}
                value={selectedUser}
                onChange={(_, v) => setSelectedUser(v)}
                getOptionLabel={(opt) => (opt?.name ? `${opt.name} (${opt.email})` : opt?.email || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { color: "#eee" }, "& label": { color: "#aaa" } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Button variant="contained" onClick={generateJson} disabled={!selectedUser || generating} sx={primaryButtonSx}>
                {generating ? <CircularProgress size={18} color="inherit" /> : "Generate JSON"}
              </Button>
              <Button variant="outlined" disabled={!rawJson} onClick={downloadCurrentJson} sx={{ ...outlinedButtonSx, ml: 1 }}>
                Download JSON
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 1 }}>
            This generates a template in-app (and pre-fills from existing <b>contract_metadata</b> if present). You can still upload a different JSON below.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
            Extract from Contract PDF (best-effort)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Button variant="outlined" component="label" sx={outlinedButtonSx}>
                Choose PDF
                <input type="file" accept="application/pdf,.pdf" hidden onChange={onPickPdf} />
              </Button>
              <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                {pdfFileName ? `Selected: ${pdfFileName}` : "No PDF selected"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Button
                variant="contained"
                onClick={extractFromPdf}
                disabled={!pdfBase64 || !payload || extractingPdf}
                sx={primaryButtonSx}
              >
                {extractingPdf ? <CircularProgress size={18} color="inherit" /> : "Extract & Fill"}
              </Button>
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 1 }}>
            Works only if the PDF has selectable text. If it is a scanned image, OCR is needed and extraction may return empty candidates.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Button variant="outlined" component="label" sx={outlinedButtonSx}>
                Choose JSON file
                <input type="file" accept=".json,application/json" hidden onChange={onPickFile} />
              </Button>
              <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                {fileName ? `Selected: ${fileName}` : "No file selected"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                disabled={!payload || loading}
                onClick={runPreview}
                sx={{ ...primaryButtonSx, mr: 1 }}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : "Preview"}
              </Button>
              <Button
                variant="contained"
                disabled={!payload || loading}
                onClick={runApply}
                sx={dangerButtonSx}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : "Apply"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {payload && (
        <Card sx={{ mb: 2, bgcolor: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
              Parsed payload
            </Typography>
            <Typography variant="body2" sx={{ color: "#aaa" }}>
              userId: <span style={{ fontFamily: "monospace" }}>{safeString(userId)}</span> · email: {safeString(userEmail)}
            </Typography>
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="overline" sx={{ color: "#aaa" }}>
                  Contract fields (to import)
                </Typography>
                <Typography sx={{ color: "#ccc" }}>contract_open_date: {safeString(contract?.contract_open_date || "—")}</Typography>
                <Typography sx={{ color: "#ccc" }}>first_wallet_address: {safeString(contract?.first_wallet_address || "—")}</Typography>
                <Typography sx={{ color: "#ccc" }}>
                  withdrawal_wallet_addresses: {Array.isArray(contract?.withdrawal_wallet_addresses) ? contract.withdrawal_wallet_addresses.length : 0}
                </Typography>
                <Typography sx={{ color: "#ccc" }}>
                  initial_deposit_amount: {contract?.initial_deposit_amount ?? "—"}
                </Typography>
                <Typography sx={{ color: "#ccc" }}>
                  fiddux_internal_wallet_address: {safeString(contract?.fiddux_internal_wallet_address || "—")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="overline" sx={{ color: "#aaa" }}>
                  Legacy event candidates (optional)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
                  <Chip label={`candidates: ${legacyCandidates.length}`} />
                  <Chip label={`selected: ${selectedCount}`} />
                  <Button size="small" variant="outlined" onClick={() => toggleAll(true)} sx={outlinedButtonSx}>
                    Select all
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => toggleAll(false)}
                    sx={{
                      color: "#fff",
                      WebkitTextFillColor: "#fff",
                      borderColor: "rgba(255,255,255,0.25)",
                      textTransform: "none",
                      "&:hover": { borderColor: "rgba(255,255,255,0.45)", backgroundColor: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ color: "#777" }}>
                  Tip: start by selecting only events relevant to pre-07/09/2022 history, after manual review.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {payload && legacyCandidates.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
              Legacy event candidates (review + select)
            </Typography>
            <TableContainer component={Paper} sx={{ bgcolor: "transparent" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "#aaa" }}>Import</TableCell>
                    <TableCell sx={{ color: "#aaa" }}>Type</TableCell>
                    <TableCell sx={{ color: "#aaa" }}>Date</TableCell>
                    <TableCell sx={{ color: "#aaa" }}>Amount</TableCell>
                    <TableCell sx={{ color: "#aaa" }}>Source file</TableCell>
                    <TableCell sx={{ color: "#aaa" }}>Context</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {legacyCandidates.slice(0, 150).map((e, idx) => {
                    const key = e.eventId || `${e.type_guess || "unknown"}|${e.amount ?? ""}|${e.date ?? ""}|${e.source_file ?? ""}|${idx}`;
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <Checkbox
                            checked={!!selectedEventIds[key]}
                            onChange={(ev) => setSelectedEventIds((s) => ({ ...s, [key]: ev.target.checked }))}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#ccc" }}>{e.type_guess || "unknown"}</TableCell>
                        <TableCell sx={{ color: "#ccc" }}>{e.date ? String(e.date).slice(0, 10) : "—"}</TableCell>
                        <TableCell sx={{ color: "#ccc" }}>{e.amount ?? "—"}</TableCell>
                        <TableCell sx={{ color: "#ccc" }}>{e.source_file || "—"}</TableCell>
                        <TableCell sx={{ color: "#777" }}>{(e.context || "").slice(0, 140)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {legacyCandidates.length > 150 && (
              <Typography variant="caption" sx={{ color: "#777", mt: 1, display: "block" }}>
                Showing first 150 candidates to keep the UI responsive.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {(preview || applyResult) && (
        <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
              Results
            </Typography>
            {preview && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: "#aaa" }}>Preview:</Typography>
                <Typography sx={{ color: "#ccc" }}>
                  Contract diffs: {Array.isArray(preview.contractDiffs) ? preview.contractDiffs.length : 0} · legacy incoming:{" "}
                  {preview.legacyEvents?.totalIncoming ?? 0}
                </Typography>
              </Box>
            )}
            {applyResult && (
              <Box>
                <Typography sx={{ color: "#aaa" }}>Apply:</Typography>
                <Typography sx={{ color: "#ccc" }}>
                  backupId: <span style={{ fontFamily: "monospace" }}>{applyResult.backupId}</span>
                </Typography>
                <Typography sx={{ color: "#ccc" }}>
                  contract fields updated: {applyResult.contractFieldsUpdated} · legacy created: {applyResult.legacyEventsCreated} · legacy updated:{" "}
                  {applyResult.legacyEventsUpdated}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 1 }}>
            Rollback
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                label="backupId"
                value={rollbackBackupId}
                onChange={(e) => setRollbackBackupId(e.target.value)}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { color: "#eee" }, "& label": { color: "#aaa" } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={runRollback}
                sx={{
                  color: "#fff",
                  WebkitTextFillColor: "#fff",
                  borderColor: "rgba(255,255,255,0.25)",
                  textTransform: "none",
                  "&:hover": { borderColor: "rgba(255,255,255,0.45)", backgroundColor: "rgba(255,255,255,0.06)" },
                  "&.Mui-disabled": { color: "#fff", WebkitTextFillColor: "#fff", opacity: 0.55 },
                }}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : "Rollback"}
              </Button>
            </Grid>
          </Grid>
          {rollbackResult && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: "#ccc" }}>
                Rolled back: {rollbackResult.contractFieldsReverted} contract fields · restored {rollbackResult.legacyEventsRestored} events · deleted{" "}
                {rollbackResult.legacyEventsDeleted} events
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {rawJson && (
        <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.15)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ color: "#aaa", mb: 1 }}>
              Raw JSON (first 2,000 chars)
            </Typography>
            <pre style={{ color: "#777", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
              {rawJson.slice(0, 2000)}
            </pre>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

